import { useCallback } from "react"
import { queryOptions, useQuery } from "@tanstack/react-query"
import { type BlueprintGraphQLClient } from "@krakentech/blueprint-auth"
import { type FragmentOf, graphql, readFragment, type VariablesOf } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { getPaginatedNodes } from "@/utils/getPaginatedNodes"
import { isAgreementInLeaveSupplierProcess } from "../utils/isAgreementInLeaveSupplierProcess"

const LeaveSupplierProcessesQuery = graphql(
    `
        query LeaveSupplierProcesses($accountNumber: String!) {
            lifecycleProcesses(accountNumber: $accountNumber, onlyActive: false) {
                leaveSupplierProcesses(first: 10) {
                    edges {
                        node {
                            ...IsAgreementInLeaveSupplierProcess_LeaveSupplierProcesses
                        }
                    }
                }
            }
        }
    `,
    [isAgreementInLeaveSupplierProcess.fragments.leaveSupplierProcesses]
)

const graphQLFragments = {
    agreement: graphql(
        `
            fragment useIsLeaveSupplierProcessAgreement_Agreement on AgreementType {
                ...IsAgreementInLeaveSupplierProcess_Agreement
            }
        `,
        [isAgreementInLeaveSupplierProcess.fragments.agreement]
    ),
}

type UseIsLeaveSupplierProcessAgreementProps = {
    accountNumber: string
}

type IsLeaveSupplierProcessAgreementProps = {
    agreement: FragmentOf<typeof graphQLFragments.agreement>
}

function leaveSupplierProcessesQueryOptions(
    client: BlueprintGraphQLClient,
    variables: VariablesOf<typeof LeaveSupplierProcessesQuery>
) {
    return queryOptions({
        queryKey: ["useIsSupplyPointInLeaveSupplierProcess", variables.accountNumber] as const,
        queryFn() {
            return client.request(LeaveSupplierProcessesQuery, variables)
        },
    })
}

export function useIsLeaveSupplierProcessAgreement({
    accountNumber,
}: UseIsLeaveSupplierProcessAgreementProps) {
    const graphQLClient = useGraphQLClient()
    const { data } = useQuery(leaveSupplierProcessesQueryOptions(graphQLClient, { accountNumber }))

    return useCallback(
        (props: IsLeaveSupplierProcessAgreementProps) => {
            const agreement = readFragment(graphQLFragments.agreement, props.agreement)

            if (!data?.lifecycleProcesses?.leaveSupplierProcesses) {
                return false
            }

            return isAgreementInLeaveSupplierProcess({
                agreement,
                leaveSupplierProcesses: getPaginatedNodes(
                    data?.lifecycleProcesses?.leaveSupplierProcesses
                ),
            })
        },
        [data?.lifecycleProcesses?.leaveSupplierProcesses]
    )
}

useIsLeaveSupplierProcessAgreement.fragments = graphQLFragments

useIsLeaveSupplierProcessAgreement.queryOptions = {
    leaveSupplierProcesses: leaveSupplierProcessesQueryOptions,
}
